import { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { deploymentProfile } from '../config/deploymentProfile';
import { downloadFontAssets } from '../utils/downloadFontAssets';

function CodePanel({ code, supportsSeaborn, outputFormat, onOutputFormatChange }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [fontStatus, setFontStatus] = useState('idle');
  const { resolved: theme } = useTheme();
  const ui = theme.ui || {};

  const handleDownloadFonts = async () => {
    if (fontStatus === 'loading') return;
    setFontStatus('loading');
    try {
      await downloadFontAssets();
      setFontStatus('done');
      setTimeout(() => setFontStatus('idle'), 2000);
    } catch (err) {
      console.error('Font bundle download failed:', err);
      setFontStatus('error');
      setTimeout(() => setFontStatus('idle'), 2000);
    }
  };

  const fontLabel = {
    idle: '↓ Fonts',
    loading: 'Zipping…',
    done: 'Saved!',
    error: 'Failed',
  }[fontStatus];

  const handleCopy = async () => {
    let success = false;

    // Try modern Clipboard API first (HTTPS/localhost)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(code);
        success = true;
      } catch (err) {
        console.log('Clipboard API failed, trying fallback');
      }
    }

    // Fallback for HTTP (seamless, no alerts)
    if (!success) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        success = document.execCommand('copy');
        document.body.removeChild(textArea);
      } catch (err) {
        console.error('Both copy methods failed:', err);
      }
    }

    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      className={`${expanded ? 'flex-[9]' : 'flex-shrink-0'} flex flex-col border-t ui-bg ui-border transition-all overflow-hidden`}
      style={{ backgroundColor: ui.bg, borderTopColor: ui.border, ...(!expanded ? { height: '16rem' } : { minHeight: '16rem' }) }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-2 border-b ui-border flex-shrink-0"
        style={{ borderBottomColor: ui.border }}
      >
        <span className="text-xs uppercase tracking-wide" style={{ color: ui.textSecondary }}>
          Generated Code
        </span>

        <div className="flex items-center gap-3">
          {supportsSeaborn && (
            <select
              value={outputFormat || 'matplotlib'}
              onChange={(e) => onOutputFormatChange && onOutputFormatChange(e.target.value)}
              className="text-xs px-2 py-1 border ui-bg ui-border"
              style={{ borderColor: ui.border }}
            >
              <option value="matplotlib">Matplotlib</option>
              <option value="seaborn">Seaborn</option>
            </select>
          )}

          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs px-3 py-1 border ui-hover ui-border"
            style={{ borderColor: ui.border }}
          >
            {expanded ? 'Collapse' : 'Expand'}
          </button>

          <button
            onClick={handleCopy}
            className={`text-xs px-3 py-1 border transition-colors ${copied ? 'ui-accent-bg' : 'ui-hover ui-border'}`}
            style={copied ? {} : { borderColor: ui.border }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>

          {deploymentProfile.isInternal && (
            <button
              onClick={handleDownloadFonts}
              disabled={fontStatus === 'loading'}
              className={`text-xs px-3 py-1 border transition-colors whitespace-nowrap ${fontStatus === 'done' ? 'ui-accent-bg' : 'ui-hover ui-border'}`}
              style={fontStatus === 'done' ? {} : { borderColor: ui.border }}
              title="Download the Cohere fonts as a .zip laid out to match this script (fonts/ + fonts/internal/), so it runs locally"
            >
              {fontLabel}
            </button>
          )}
        </div>
      </div>

      {/* Code content */}
      <div className="flex-1 p-4 ui-bg2 overflow-y-auto min-h-0" style={{ backgroundColor: ui.bgSecondary }}>
        <pre className="text-xs leading-relaxed whitespace-pre m-0">
          <code>
            <SyntaxHighlight code={code} />
          </code>
        </pre>
      </div>
    </div>
  );
}

// Simple Python syntax highlighting
function SyntaxHighlight({ code }) {
  if (!code) return null;

  // Split into lines and process each
  const lines = code.split('\n');
  
  return (
    <>
      {lines.map((line, i) => (
        <div key={i}>
          {highlightLine(line)}
        </div>
      ))}
    </>
  );
}

function highlightLine(line) {
  // Handle comments
  if (line.trim().startsWith('#')) {
    return <span className="code-comment">{line}</span>;
  }

  // Simple keyword highlighting
  const keywords = ['import', 'from', 'as', 'def', 'return', 'if', 'else', 'elif', 'for', 'in', 'try', 'except', 'True', 'False', 'None'];
  
  let result = line;
  
  // Highlight strings (simplified - just handles single and double quotes)
  result = result.replace(/(["'])((?:\\.|[^\\])*?)\1/g, '<string>$1$2$1</string>');
  
  // Highlight numbers
  result = result.replace(/\b(\d+\.?\d*)\b/g, '<number>$1</number>');
  
  // Highlight keywords
  keywords.forEach(kw => {
    const regex = new RegExp(`\\b(${kw})\\b`, 'g');
    result = result.replace(regex, '<keyword>$1</keyword>');
  });

  // Convert to React elements
  const parts = result.split(/(<\/?(?:string|number|keyword)>)/);
  let currentTag = null;
  
  return parts.map((part, i) => {
    if (part === '<string>') {
      currentTag = 'string';
      return null;
    } else if (part === '</string>') {
      currentTag = null;
      return null;
    } else if (part === '<number>') {
      currentTag = 'number';
      return null;
    } else if (part === '</number>') {
      currentTag = null;
      return null;
    } else if (part === '<keyword>') {
      currentTag = 'keyword';
      return null;
    } else if (part === '</keyword>') {
      currentTag = null;
      return null;
    }

    if (currentTag === 'string') {
      return <span key={i} className="code-string">{part}</span>;
    } else if (currentTag === 'number') {
      return <span key={i} className="code-number">{part}</span>;
    } else if (currentTag === 'keyword') {
      return <span key={i} className="code-keyword">{part}</span>;
    }
    
    return <span key={i}>{part}</span>;
  });
}

export default CodePanel;
